import { RefObject, useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Editor as EditorRef } from "@tinymce/tinymce-react";
import {
  Stack,
  Typography,
  Grid,
  Select,
  MenuItem,
  SelectChangeEvent,
  Divider,
  Box,
} from "@mui/material";
import {
  selectedValuesAtom,
  selectedVoiceLangAtom,
  voicesDescriptionAtom,
  languageOptionsAtom,
} from "atoms";
import { useAtom, useAtomValue } from "jotai";
import { SSMLTag, SSMLTagsColors } from "shared/constants";
import {
  handleSSMLValueClick,
  isEditorEmpty,
  calculateAvailableHeight,
  useEditorSelection,
} from "shared/utils/tts-editor-helper";
import { getCurrentSSMLValue } from "features/TTS/TTSCustomEditor/utils/editorKeyDownHandler";
import { useIsActiveSSMLTag } from "shared/hooks/useSSMLTag";
import SSMLPreview from "features/TTS/TTSCustomEditor/components/SSMLPreview";
import { useSSMLMenuDisabled } from "shared/utils/menu-disabling-rules";
import VoiceItem from "features/TTS/components/VoiceItem";
import CustomIconTextButtonMenu from "../../components/CustomIconTextButtonMenu";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import FilterListIcon from "@mui/icons-material/FilterList";
import { getCategorizedVoices } from "shared/utils/tts";
import { usePlayVoice } from "shared/hooks/usePlayVoice";

interface VoiceMenuProps {
  editorRef: RefObject<EditorRef | null>;
  disabled?: boolean;
}

export default function VoiceMenu({
  editorRef,
  disabled,
}: VoiceMenuProps): JSX.Element {
  const { t } = useTranslation();
  const isVoiceTagActive = useIsActiveSSMLTag(SSMLTag.Voice);
  const selectedValues = useAtomValue(selectedValuesAtom);
  const [selectedVoiceLang, setSelectedVoiceLang] = useAtom(
    selectedVoiceLangAtom
  );
  const voicesDescription = useAtomValue(voicesDescriptionAtom);
  const languageOptions = useAtomValue(languageOptionsAtom);
  const isSelectionDisabled = useSSMLMenuDisabled(editorRef, SSMLTag.Voice);
  const { handlePlayClick } = usePlayVoice();
  const editorEmpty = isEditorEmpty(editorRef);
  const hasSelection = useEditorSelection(editorRef);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogHeight, setDialogHeight] = useState(0);
  const [selectedVoice, setSelectedVoice] = useState("");
  const [initialVoiceState, setInitialVoiceState] = useState({
    voice: "",
    lang: "",
  });
  const [tagLanguage, setTagLanguage] = useState("");
  const [voiceFilterLanguage, setVoiceFilterLanguage] = useState("All");

  const [voices, setVoices] = useState<string[]>([]);
  const [allAvailableVoices, setAllAvailableVoices] = useState<string[]>([]);

  const [audioPlayer, setAudioPlayer] = useState<HTMLAudioElement>();
  const [playbackState, setPlaybackState] = useState({
    playing: "",
    progress: "",
  });

  const headerRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollableContentRef = useRef<HTMLDivElement>(null);
  const voiceItemRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const getEffectiveLanguage = (): string => {
    if (selectedVoiceLang !== "automatic") {
      return selectedVoiceLang;
    }

    // When automatic, check for language tag first
    if (editorRef.current?.editor) {
      const currentLangValue = getCurrentSSMLValue(editorRef, SSMLTag.Language);
      if (currentLangValue) {
        return currentLangValue;
      }
    }

    // Fallback to script settings
    return selectedValues.selectedLanguage;
  };

  const handleVoiceItemPlay = (voice: string) => {
    const effectiveLanguage = getEffectiveLanguage();

    handlePlayClick(
      voice,
      effectiveLanguage,
      audioPlayer,
      setAudioPlayer,
      (name) => setPlaybackState((prev) => ({ ...prev, playing: name })),
      (name) => setPlaybackState((prev) => ({ ...prev, progress: name })),
      playbackState.playing
    );
  };

  // Add automatic option to language options
  const automaticOption = {
    value: "automatic",
    name: t("tts.toolbarMenu.languageMenu.automatic"),
  };
  const languageOptionsWithAutomatic = [automaticOption, ...languageOptions];

  const hasChanges =
    isVoiceTagActive &&
    (selectedVoice !== initialVoiceState.voice ||
      selectedVoiceLang !== initialVoiceState.lang);
  const filterLanguageOptions = [
    { value: "All", name: "All" },
    ...languageOptions,
  ];

  const isDisabled =
    disabled ||
    editorEmpty ||
    isSelectionDisabled ||
    (!hasSelection && !isVoiceTagActive);

  useEffect(() => {
    const allVoices = languageOptions.flatMap((option) => {
      const { native, nonNative } = getCategorizedVoices(
        option.value,
        voicesDescription
      );
      return [...native, ...nonNative];
    });
    setAllAvailableVoices([...new Set(allVoices)]);
  }, [voicesDescription, languageOptions]);

  // Filter voices based on language
  useEffect(() => {
    if (voiceFilterLanguage === "All") {
      setVoices(allAvailableVoices);
    } else {
      const { native } = getCategorizedVoices(
        voiceFilterLanguage,
        voicesDescription
      );
      setVoices(native);
    }
  }, [voiceFilterLanguage, allAvailableVoices, voicesDescription]);

  // Set default language to "automatic" when no tag is active
  useEffect(() => {
    if (selectedVoiceLang === "" && !isVoiceTagActive && !tagLanguage) {
      setSelectedVoiceLang("automatic");
    }
  }, [
    selectedValues.selectedLanguage,
    selectedVoiceLang,
    setSelectedVoiceLang,
    isVoiceTagActive,
    tagLanguage,
  ]);

  useEffect(() => {
    if (isVoiceTagActive && editorRef.current?.editor) {
      const currentValue = getCurrentSSMLValue(editorRef, SSMLTag.Voice);
      if (currentValue) {
        const [voiceName, voiceLang] = currentValue.split(", ");
        setSelectedVoice(voiceName);
        setSelectedVoiceLang(voiceLang || "automatic");
        setTagLanguage(voiceLang || "automatic");
        setInitialVoiceState({
          voice: voiceName,
          lang: voiceLang || "automatic",
        });
      }
    } else if (!isVoiceTagActive) {
      // Reset tag language when no tag is active
      setTagLanguage("");
    }
  }, [isVoiceTagActive, editorRef, setSelectedVoiceLang]);

  // Calculate dialog height
  useEffect(() => {
    const updateHeight = () => {
      setDialogHeight(calculateAvailableHeight(containerRef, headerRef));
    };

    updateHeight();
    const resizeObserver = new ResizeObserver(updateHeight);
    if (containerRef.current) resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    // Function to scroll to the selected voice
    const scrollToSelectedVoice = () => {
      const element = voiceItemRefs.current.get(selectedVoice);
      if (element && scrollableContentRef.current) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    };

    if (dialogOpen && selectedVoice && voices.length > 0) {
      setTimeout(() => {
        setTimeout(scrollToSelectedVoice, 200);
      }, 100);
    }
  }, [dialogOpen, selectedVoice, voices.length]);

  const handleVoiceSelection = () => {
    if (!editorRef) return;

    // If 'automatic' is selected, omit lang attribute
    const value =
      selectedVoiceLang === "automatic"
        ? selectedVoice
        : `${selectedVoice}, ${selectedVoiceLang}`;
    handleSSMLValueClick(
      editorRef,
      SSMLTag.Voice,
      value,
      SSMLTagsColors.VoiceTag,
      [SSMLTag.Voice]
    );
    setTagLanguage(selectedVoiceLang === "automatic" ? "" : selectedVoiceLang);
    setInitialVoiceState({ voice: selectedVoice, lang: selectedVoiceLang });
  };

  const handleLanguageChange = (event: SelectChangeEvent) => {
    setSelectedVoiceLang(event.target.value);
  };

  const handleVoiceFilterChange = (event: SelectChangeEvent) => {
    setVoiceFilterLanguage(event.target.value);
  };

  const onDialogClose = () => {
    setDialogOpen(false);

    if (!isVoiceTagActive) {
      setSelectedVoice("");
      if (!tagLanguage) {
        setSelectedVoiceLang("automatic");
      }
    }
  };

  const onDialogOpen = () => {
    setVoiceFilterLanguage("All");
    if (isVoiceTagActive && editorRef.current?.editor) {
      const currentValue = getCurrentSSMLValue(editorRef, SSMLTag.Voice);
      if (currentValue) {
        const [voiceName, voiceLang] = currentValue.split(", ");
        setSelectedVoice(voiceName);
        setSelectedVoiceLang(voiceLang || "automatic");
        setTagLanguage(voiceLang || "automatic");
        setInitialVoiceState({
          voice: voiceName,
          lang: voiceLang || "automatic",
        });
      }
    }
    setDialogOpen(true);
  };

  return (
    <CustomIconTextButtonMenu
      useDialog
      componentId="voice"
      menuTitle={t("tts.toolbarMenu.voiceMenu.title")}
      dialogHeader={t("tts.toolbarMenu.voiceMenu.title")}
      isActive={isVoiceTagActive}
      onDialogPrimaryButtonClick={handleVoiceSelection}
      isDialogPrimaryButtonDisabled={
        isVoiceTagActive ? !hasChanges : !selectedVoice
      }
      onDialogClose={onDialogClose}
      onDialogOpen={onDialogOpen}
      isEditorEmpty={editorEmpty}
      shouldBeDisabled={isDisabled}
      editorRef={editorRef}
      tagType={SSMLTag.Voice}
      showClearButton={isVoiceTagActive}
      icon={PersonOutlineIcon}
      isSelectionDisabled={isSelectionDisabled}
      footerText={
        <SSMLPreview
          type="voice"
          voiceAttributes={{
            name: selectedVoice,
            language:
              selectedVoiceLang === "automatic" ? undefined : selectedVoiceLang,
          }}
        />
      }
    >
      <Box
        ref={containerRef}
        sx={{
          height: "100% !important",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          ref={headerRef}
          sx={{
            backgroundColor: "background.paper",
            zIndex: 10,
            flexShrink: 0,
          }}
        >
          <Stack direction="row" justifyContent="space-between" spacing={6}>
            <Stack sx={{ width: "100%" }}>
              <Typography variant="subtitle1" paddingBottom={1}>
                {t("tts.toolbarMenu.voiceMenu.textLanguage")}
              </Typography>
              <Select
                id="text-language"
                value={
                  languageOptionsWithAutomatic.find(
                    (opt) =>
                      opt.value.toLowerCase() ===
                      selectedVoiceLang.toLowerCase()
                  )?.value || ""
                }
                onChange={handleLanguageChange}
              >
                {languageOptionsWithAutomatic.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.name}
                  </MenuItem>
                ))}
              </Select>
            </Stack>
            <Stack sx={{ width: "100%" }}>
              <Typography variant="subtitle1" paddingBottom={1} display="flex">
                <FilterListIcon sx={{ marginRight: 1 }} />
                {t("tts.toolbarMenu.voiceMenu.voiceOrigin")}
              </Typography>
              <Select
                id="voice-origin"
                value={voiceFilterLanguage}
                onChange={handleVoiceFilterChange}
              >
                {filterLanguageOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.name}
                  </MenuItem>
                ))}
              </Select>
            </Stack>
          </Stack>
          <Divider sx={{ my: 3, mx: -3 }} />
        </Box>

        <Box
          ref={scrollableContentRef}
          sx={{
            height: dialogHeight ? `${dialogHeight}px` : "80vh",
            overflowY: "auto",
            overflowX: "hidden",
            pr: 1,
            flexGrow: 1,
          }}
        >
          {voices.length > 0 ? (
            <Grid container spacing={2}>
              {voices.map((voice) => (
                <Grid item xs={12} md={6} lg={4} key={voice}>
                  <Box
                    ref={(el: HTMLDivElement | null) => {
                      if (el) voiceItemRefs.current.set(voice, el);
                    }}
                  >
                    <VoiceItem
                      voice={voice}
                      handleVoiceSelection={() => setSelectedVoice(voice)}
                      isSelected={selectedVoice === voice}
                      handlePlayClick={() => handleVoiceItemPlay(voice)}
                      showStopButton={playbackState.playing === voice}
                      showInProgress={playbackState.progress === voice}
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Stack justifyContent="center" alignItems="center" pt={2} pb={2}>
              <Typography variant="body1" color="text.secondary">
                {t("tts.catalog.noSpeakerFound", {
                  what: voiceFilterLanguage || selectedVoiceLang,
                })}
              </Typography>
            </Stack>
          )}
        </Box>
      </Box>
    </CustomIconTextButtonMenu>
  );
}
